import { ScoreBand } from '../types';

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const decimalPlaces = (value: number): number => {
  const [, decimals = ''] = value.toString().split('.');
  return decimals.length;
};

const getBandEdges = (
  index: number,
  min: number,
  max: number,
  bands: ScoreBand[],
): [number, number] => {
  const band = bands[index];
  const previousBand = bands[index - 1];
  const nextBand = bands[index + 1];

  const lowerEdge = previousBand
    ? ((previousBand.max ?? band.min ?? min) + (band.min ?? previousBand.max ?? min)) / 2
    : min;
  const upperEdge = nextBand
    ? ((band.max ?? nextBand.min ?? max) + (nextBand.min ?? band.max ?? max)) / 2
    : max;

  return [clamp(lowerEdge, min, max), clamp(upperEdge, min, max)];
};

const getBandIndexForValue = (
  value: number,
  min: number,
  max: number,
  bands: ScoreBand[],
): number => {
  const clinicalBandIndex = bands.findIndex((band) => {
    const meetsMinimum = band.min === undefined || value >= band.min;
    const meetsMaximum = band.max === undefined || value <= band.max;
    return meetsMinimum && meetsMaximum;
  });

  if (clinicalBandIndex >= 0) return clinicalBandIndex;

  const fallbackIndex = bands.findIndex((_, index) => {
    const [lowerEdge, upperEdge] = getBandEdges(index, min, max, bands);
    return value >= lowerEdge && value <= upperEdge;
  });

  return fallbackIndex >= 0 ? fallbackIndex : value <= min ? 0 : bands.length - 1;
};

export const normalizeSliderValue = (
  value: number,
  min: number,
  max: number,
  step: number,
): number => {
  const safeStep = step > 0 ? step : 1;
  const steppedValue = min + Math.round((value - min) / safeStep) * safeStep;
  const precision = Math.max(decimalPlaces(min), decimalPlaces(safeStep));
  return clamp(Number(steppedValue.toFixed(precision)), min, max);
};

export const valueToSliderPosition = (
  value: number,
  min: number,
  max: number,
  bands: ScoreBand[],
): number => {
  if (bands.length === 0 || max <= min) return 0;

  const clampedValue = clamp(value, min, max);
  const bandIndex = getBandIndexForValue(clampedValue, min, max, bands);
  const [lowerEdge, upperEdge] = getBandEdges(bandIndex, min, max, bands);
  const bandRange = upperEdge - lowerEdge;
  const bandProgress = bandRange <= 0 ? 0.5 : (clampedValue - lowerEdge) / bandRange;

  return ((bandIndex + clamp(bandProgress, 0, 1)) / bands.length) * 100;
};

export const sliderPositionToValue = (
  positionPercent: number,
  min: number,
  max: number,
  bands: ScoreBand[],
  step = 1,
): number => {
  if (bands.length === 0 || max <= min) return min;

  const normalizedPosition = clamp(positionPercent, 0, 100) / 100;
  const scaledPosition = normalizedPosition * bands.length;
  const bandIndex = normalizedPosition === 1
    ? bands.length - 1
    : Math.floor(scaledPosition);
  const bandProgress = normalizedPosition === 1 ? 1 : scaledPosition - bandIndex;
  const [lowerEdge, upperEdge] = getBandEdges(bandIndex, min, max, bands);
  const rawValue = lowerEdge + (upperEdge - lowerEdge) * bandProgress;

  return normalizeSliderValue(rawValue, min, max, step);
};
